import country from 'country-list-js'
import { Button } from '@/components/ui'
import useGdpData from './hooks/useGdpData'
import { useParams } from 'react-router-dom'
import { Input } from '@/components/ui/input'
import CountriesTable from './CountriesTable'
import SelectedCountries from './SelectedCountries'
import { GeolocationIcon } from '@/components/icons'
import { ChevronUp, ChevronDown, LoaderCircle } from 'lucide-react'
import { InfoIcon } from '@/components/icons/InfoIcon'
import { useMemo, useState, useCallback } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import SearchInput from '@/components/common/SearchInput'
import useAudienceTargetingStore from '@/store/audienceTargetingStore'
import { useColumnPreferences } from '../../hooks/useColumnPreferences'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip.jsx'
import { GET_LOCATION_PRESETS, CREATE_LOCATION_PRESET, DELETE_LOCATION_PRESET } from '@/graphql/location-preset-queries'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import ConfirmationDialog from '@/components/common/ConfirmationDialog'

import 'react-flagpack/dist/style.css'

const priorityCountries = [
    'United States',
    'United Kingdom',
    'Canada',
    'Australia',
    'Germany',
    'France',
    'Italy',
    'Spain',
]

const LocationSettings = ({
    stage,
    audience,
    metricsDataLoading,
    formatMetricValue,
    metricsData,
    setStepContentData,
    getStepContentData,
}) => {
    const { getCountrySettings, addIncludedCountry, addExcludedCountry, removeCountry } = useAudienceTargetingStore()
    const { loading: gdpLoading, error: gdpError, formatGdp, getGdpByIso3 } = useGdpData()

    const countrySettings = getCountrySettings(stage, audience)
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' })
    const [searchTerm, setSearchTerm] = useState('')
    const [filterBy, setFilterBy] = useState('spend')
    const [minValue, setMinValue] = useState('')
    const [maxValue, setMaxValue] = useState('')
    const [isSaveLocationModalOpen, setIsSaveLocationModalOpen] = useState(false)
    const [presetName, setPresetName] = useState('')
    const [selectedPresetId, setSelectedPresetId] = useState('')
    const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)
    const [presetToDelete, setPresetToDelete] = useState(null)
    const { id: campaignId } = useParams()

    const { data: presetsData, refetch: refetchPresets } = useQuery(GET_LOCATION_PRESETS, {
        variables: { campaignId },
    })
    const [createLocationPreset, { loading: isSaving }] = useMutation(CREATE_LOCATION_PRESET)
    const [deleteLocationPreset, { loading: isDeleting }] = useMutation(DELETE_LOCATION_PRESET)

    // Column preferences hook
    const {
        selectedColumns,
        allMetrics,
        updateSelectedColumns,
        isLoading: allMetricsLoading,
    } = useColumnPreferences('Location', setStepContentData, getStepContentData)

    // Handle metric replacement for a specific column index
    const handleMetricReplace = (columnIndex, newMetric) => {
        const currentMetrics = [...selectedColumns]
        currentMetrics[columnIndex] = newMetric
        updateSelectedColumns(currentMetrics)
    }

    // Process metrics data to organize by country code
    const countryMetrics = useMemo(() => {
        const metrics = {}
        if (metricsData) {
            metricsData.forEach((metric) => {
                const countryCode = metric.breakdownValue
                if (!metrics[countryCode]) metrics[countryCode] = {}
                // Store all metric values by their API key
                metrics[countryCode][metric.metricApiKey] = metric.value
            })
        }
        return metrics
    }, [metricsData])

    // Convert array-based settings to Map for component compatibility
    const countryStates = useMemo(() => {
        const states = new Map()
        countrySettings.included.forEach((code) => states.set(code, 'included'))
        countrySettings.excluded.forEach((code) => states.set(code, 'excluded'))
        return states
    }, [countrySettings])

    // Helper function to get metric value for a country
    const getCountryMetricValue = useCallback(
        (countryCode, metricApiKey) => {
            return countryMetrics[countryCode]?.[metricApiKey] || 0
        },
        [countryMetrics],
    )

    const countries = useMemo(() => {
        const allCountries = Object.values(country.all)

        // Create a map for quick lookup of country objects by name
        const countryMap = new Map(allCountries.map((country) => [country.name, country]))

        // Get priority countries in the exact order defined
        const priority = priorityCountries.map((name) => countryMap.get(name)).filter(Boolean) // Remove any undefined values if country name not found

        // Get other countries sorted alphabetically
        const others = allCountries
            .filter((country) => !priorityCountries.includes(country.name))
            .sort((a, b) => a.name.localeCompare(b.name))

        // Add GDP data to each country
        const enrichedCountries = [...priority, ...others].map((country) => ({
            ...country,
            gdp: getGdpByIso3(country.iso3),
            formattedGdp: formatGdp(getGdpByIso3(country.iso3)),
        }))

        // Return priority countries first, then alphabetically sorted others
        return enrichedCountries
    }, [getGdpByIso3, formatGdp])

    const sortedCountries = useMemo(() => {
        // Helper function to check if a country passes the filter criteria
        const passesFilter = (country) => {
            // If no filter is selected, show all countries
            if (!filterBy) return true

            const metricValue = getCountryMetricValue(country.iso2, filterBy)
            const numericValue = parseFloat(metricValue) || 0

            // Apply min value filter
            if (minValue && parseFloat(minValue) > numericValue) return false

            // Apply max value filter
            if (maxValue && parseFloat(maxValue) < numericValue) return false

            return true
        }

        // First filter by search term
        let filteredCountries = countries
        if (searchTerm.trim()) {
            filteredCountries = countries.filter(
                (country) =>
                    country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    country.iso2.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    country.iso3.toLowerCase().includes(searchTerm.toLowerCase()),
            )
        }

        // Apply metric filters
        filteredCountries = filteredCountries.filter(passesFilter)

        // Then apply sorting
        if (!sortConfig.key) return filteredCountries

        return [...filteredCountries].sort((a, b) => {
            let aValue = a[sortConfig.key]
            let bValue = b[sortConfig.key]

            // Special handling for GDP sorting (use numeric values)
            if (sortConfig.key === 'gdp') {
                aValue = a.gdp || 0
                bValue = b.gdp || 0
            } else if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase()
                bValue = bValue.toLowerCase()
            }

            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1

            return 0
        })
    }, [countries, sortConfig, searchTerm, filterBy, minValue, maxValue, getCountryMetricValue])

    const handleSort = (key) => {
        let direction = 'asc'
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc'
        }

        setSortConfig({ key, direction })
    }

    const handleIncludeClick = (countryCode) => {
        const currentState = countryStates.get(countryCode)

        if (currentState === 'included') removeCountry(stage, audience, countryCode)
        else addIncludedCountry(stage, audience, countryCode)

        setSelectedPresetId('')
        setPresetName('')
    }

    const handleExcludeClick = (countryCode) => {
        const currentState = countryStates.get(countryCode)

        if (currentState === 'excluded') removeCountry(stage, audience, countryCode)
        else addExcludedCountry(stage, audience, countryCode)

        setSelectedPresetId('')
        setPresetName('')
    }

    const handleRemoveCountry = (countryCode) => {
        removeCountry(stage, audience, countryCode)
        setSelectedPresetId('')
        setPresetName('')
    }

    const handleSavePreset = async () => {
        await createLocationPreset({
            variables: {
                input: {
                    name: presetName,
                    included_countries: countrySettings.included,
                    excluded_countries: countrySettings.excluded,
                    campaign_id: campaignId,
                },
            },
        })

        refetchPresets()
        setIsSaveLocationModalOpen(false)
        setPresetName('')
    }

    const handleCloseModal = () => {
        setIsSaveLocationModalOpen(false)
        setPresetName('')
    }

    const handleLoadPreset = (presetId) => {
        const preset = presetsData?.LocationPreset?.locationPresets?.find((p) => p.id === presetId)
        if (preset) {
            const includedCountries =
                typeof preset.included_countries === 'string'
                    ? JSON.parse(preset.included_countries)
                    : preset.included_countries || []
            const excludedCountries =
                typeof preset.excluded_countries === 'string'
                    ? JSON.parse(preset.excluded_countries)
                    : preset.excluded_countries || []

            // Clear current selections
            countrySettings.included.forEach((code) => removeCountry(stage, audience, code))
            countrySettings.excluded.forEach((code) => removeCountry(stage, audience, code))

            // Add preset countries
            includedCountries.forEach((code) => addIncludedCountry(stage, audience, code))
            excludedCountries.forEach((code) => addExcludedCountry(stage, audience, code))
        }
    }

    const handlePresetDeletion = (presetId) => {
        const preset = presetsData?.LocationPreset?.locationPresets?.find((p) => p.id === presetId)
        if (preset) {
            setPresetToDelete(preset)
            setIsDeleteConfirmationOpen(true)
        }
    }

    const handleConfirmDelete = async () => {
        if (presetToDelete) {
            await deleteLocationPreset({
                variables: { id: presetToDelete.id },
            })

            // Clear selected preset if it's the one being deleted
            if (selectedPresetId === presetToDelete.id) {
                setSelectedPresetId('')
            }

            await refetchPresets()
            setIsDeleteConfirmationOpen(false)
            setPresetToDelete(null)
        }
    }

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) return ''

        return sortConfig.direction === 'asc' ? (
            <ChevronUp className="tw:w-4 tw:h-4" />
        ) : (
            <ChevronDown className="tw:w-4 tw:h-4" />
        )
    }

    return (
        <div className="tw:flex tw:gap-6 tw:flex-col tw:bg-secondary-background tw:rounded-xs">
            <div className="tw:flex tw:gap-1.5 tw:items-center tw:p-3 tw:pb-0">
                <span className="tw:text-gray-200">
                    <GeolocationIcon />
                </span>
                <span className="tw:font-semibold">Locations</span>
                <Tooltip>
                    <TooltipTrigger>
                        <InfoIcon className="tw:w-5 tw:h-5 tw:text-gray-200" />
                    </TooltipTrigger>
                    <TooltipContent showArrow={false}>
                        <div className="tw:text-sm tw:text-gray-400 tw:!mb-0">
                            Target your ads based on age demographics.
                            <br />
                            <span className="tw:text-white tw:font-medium tw:block tw:mt-2">
                                At least one age range must be selected
                            </span>
                        </div>
                    </TooltipContent>
                </Tooltip>
            </div>

            <div className="tw:flex tw:gap-4 tw:px-3 tw:items-end">
                <div className="tw:flex tw:flex-col tw:gap-1 tw:flex-2">
                    <Select
                        value={selectedPresetId}
                        onValueChange={(presetId) => {
                            setSelectedPresetId(presetId)
                            handleLoadPreset(presetId)
                        }}
                    >
                        <SelectTrigger className="tw:bg-transparent tw:w-full tw:border-slate-600 tw:text-white">
                            <SelectValue placeholder="Load location selection" />
                        </SelectTrigger>
                        <SelectContent className="tw:bg-primary tw:border-slate-700">
                            {presetsData?.LocationPreset?.locationPresets?.map((preset) => (
                                <SelectItem
                                    key={preset.id}
                                    value={preset.id}
                                    allowDeletion={true}
                                    handleDelete={() => handlePresetDeletion(preset.id)}
                                    className="tw:text-white tw:hover:bg-gray-500"
                                >
                                    {preset.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <Button
                    variant="secondary"
                    className="tw:w-[175px]"
                    onClick={() => setIsSaveLocationModalOpen(true)}
                    disabled={countryStates.size === 0}
                >
                    Save Location Selection
                </Button>
            </div>

            <SelectedCountries countryStates={countryStates} onRemoveCountry={handleRemoveCountry} />

            {/* Filter Section */}
            <div className="tw:flex tw:gap-4 tw:px-3 tw:items-end">
                <div className="tw:flex tw:flex-col tw:gap-1 tw:flex-3">
                    <label className="tw:text-sm tw:font-semibold">Filter By</label>
                    <Select value={filterBy} onValueChange={setFilterBy}>
                        <SelectTrigger className="tw:bg-transparent tw:w-full tw:border-slate-600 tw:text-white">
                            <SelectValue placeholder="Amount Spent" />
                        </SelectTrigger>
                        <SelectContent className="tw:bg-primary tw:border-slate-700">
                            {allMetrics.map((metric) => (
                                <SelectItem
                                    key={metric.id}
                                    value={metric.apiKey}
                                    className="tw:text-white tw:hover:bg-gray-500"
                                >
                                    {metric.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="tw:flex tw:flex-col tw:gap-1 tw:flex-1">
                    <label className="tw:text-sm tw:font-semibold">Min</label>
                    <Input
                        className="tw:min-w-22"
                        placeholder="0"
                        value={minValue}
                        onChange={(e) => setMinValue(e.target.value)}
                        type="number"
                    />
                </div>

                <div className="tw:flex tw:flex-col tw:gap-1 tw:flex-1">
                    <label className="tw:text-sm tw:font-semibold">Max</label>
                    <Input
                        className="tw:min-w-22"
                        placeholder="15,000"
                        value={maxValue}
                        onChange={(e) => setMaxValue(e.target.value)}
                        type="number"
                    />
                </div>
            </div>

            <div className="tw:flex tw:gap-4 tw:px-3">
                <SearchInput
                    value={searchTerm}
                    allowHotkey={false}
                    onChange={setSearchTerm}
                    placeholder="Search by country name"
                />
            </div>
            <div className="tw:border-t">
                <CountriesTable
                    gdpError={gdpError}
                    allMetrics={allMetrics}
                    gdpLoading={gdpLoading}
                    handleSort={handleSort}
                    searchTerm={searchTerm}
                    getSortIcon={getSortIcon}
                    countryStates={countryStates}
                    countryMetrics={countryMetrics}
                    selectedColumns={selectedColumns}
                    sortedCountries={sortedCountries}
                    formatMetricValue={formatMetricValue}
                    allMetricsLoading={allMetricsLoading}
                    handleIncludeClick={handleIncludeClick}
                    handleExcludeClick={handleExcludeClick}
                    metricsDataLoading={metricsDataLoading}
                    handleMetricReplace={handleMetricReplace}
                />
            </div>

            <Dialog open={isSaveLocationModalOpen} onOpenChange={setIsSaveLocationModalOpen}>
                <DialogContent className="tw:w-[540px] tw:!rounded-sm">
                    <DialogHeader showCloseButton={true} wrapperClassName="tw:h-16">
                        <DialogTitle>Save Location Selection</DialogTitle>
                    </DialogHeader>
                    <div className="tw:px-6 tw:py-10">
                        <Input
                            className="tw:h-10"
                            value={presetName}
                            placeholder="New Location Preset"
                            onChange={(e) => setPresetName(e.target.value)}
                        />
                    </div>
                    <DialogFooter className="tw:border-t tw:border-slate-700 tw:px-6 tw:py-4">
                        <Button variant="secondary" className="tw:w-[178px]" onClick={handleCloseModal}>
                            Close
                        </Button>
                        <Button className="tw:w-[178px]" onClick={handleSavePreset} disabled={isSaving}>
                            <div className="tw:relative tw:flex tw:items-center tw:justify-center">
                                <LoaderCircle
                                    className={`tw:w-4 tw:h-4 tw:animate-spin tw:absolute ${isSaving ? 'tw:opacity-100' : 'tw:opacity-0'}`}
                                />
                                <span className={isSaving ? 'tw:opacity-0' : 'tw:opacity-100'}>Save</span>
                            </div>
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ConfirmationDialog
                isOpen={isDeleteConfirmationOpen}
                onClose={() => {
                    setIsDeleteConfirmationOpen(false)
                    setPresetToDelete(null)
                }}
                onConfirm={handleConfirmDelete}
                title="Delete Location Preset"
                description={`Are you sure you want to delete "${presetToDelete?.name}"?`}
                confirmText="Delete"
                cancelText="Cancel"
                variant="destructive"
                isProcessing={isDeleting}
            />
        </div>
    )
}

export default LocationSettings
